namespace app.documentos {
    "use strict";

    import IHttpService = angular.IHttpService;
    import IHttpResponse = angular.IHttpPromiseCallbackArg;
    import IQService = angular.IQService;
    import IPromise = angular.IPromise;
    import ICookies = angular.cookies.ICookiesService;

    export interface JwtToken {
        token: string;
    }
    
    export interface History {
        created: string;
        key: string;
        user: UserHistory;
        version: string;
        serverVersion: string;
        changes: Object[];
    }
    
    export interface UserHistory {
        id: string;
        name: string;
    }
    
    export interface Edicao {
        numero: string;
        ativo: boolean;
        usuarios: string[];
    }
    
    export class OnlyofficeService {

        /** @ngInject **/
        constructor(private $http: IHttpService, private $q : IQService,
                    private properties: app.support.constants.Properties, private $cookies: ICookies) { }

        public recuperarDocumentoId(id: number, context: string): IPromise<number> {
            return this.$http.get(`${this.properties.apiUrl}/${context}/api/textos/${id}/documento`)
                .then((response: ng.IHttpPromiseCallbackArg<number>) => {
                    return response.data;
                });
        }

        public criarUrlConteudoDocumento(id: number | string, temporario: boolean, context: string) {
            return this.getBaseUrl(context).then((baseUrl) => {
                return `${baseUrl}/api/textos/documentos/${this.tempUrl(temporario)}${id}/conteudo.docx?` +
                        this.accessTokenParam();
            });
        }

        public gerarNumeroEdicao(id: number | string, temporario: boolean, context: string) : IPromise<any> {
            return this.$http.put(
                    `${this.properties.apiUrl}/${context}/api/textos/documentos/${this.tempUrl(temporario)}${id}/numero-edicao`,
                    null)
                .then((response) => {
                    return response.data;
                });
        }
        
        public excluirNumeroEdicao(id: number | string, temporario: boolean, context: string): IPromise<{}> {
            return this.$http.delete(
                `${this.properties.apiUrl}/${context}/api/textos/documentos/${this.tempUrl(temporario)}${id}/numero-edicao`);
        }

        public recuperarNumeroEdicao(id: number | string, temporario: boolean, context: string): IPromise<Edicao> {
            return this.$http.get(
                    `${this.properties.apiUrl}/${context}/api/textos/documentos/${this.tempUrl(temporario)}${id}/numero-edicao`)
                .then((response: IHttpResponse<Edicao>) => {
                    return response.data;
                });
        }

        public recuperarUrlCallback(id: number | string, temporario: boolean, save: boolean, context: string, omitToken: boolean) {
            return this.$q.when(this.getBaseUrl(context)).then((baseUrl) => {
                return `${baseUrl}/api/textos/documentos/${this.tempUrl(temporario)}${id}/integracao-onlyoffice?` +
                       `${omitToken ? "" : this.accessTokenParam()}${this.saveParam(save)}`;
            });
        }

        public recuperarUrlChanges(id: number | string, version: number, context: string): ng.IPromise<string> {
            return this.$q.when(
					`${this.properties.apiUrl}/${context}/api/textos/documentos/${id}/conteudo.docx/${version}/changes.zip?` +
					`${this.accessTokenParam()}`);
        }
        
        public existeChanges(id: number | string, version: number, context: string): ng.IPromise<boolean> {
            return this.$http.get(`${this.properties.apiUrl}/${context}` +
					`/api/textos/documentos/${id}/conteudo.docx/${version}/changes.zip/existe?` +
					`${this.accessTokenParam()}`).then((response: ng.IHttpPromiseCallbackArg<boolean>) => {
                return response.data;
            });
        }
        
        public recuperarUrlDocumentVersion(id: number | string, version: number, context: string): ng.IPromise<string> {
            return this.$q.when(this.getBaseUrl(context)).then((baseUrl) => {
                return `${baseUrl}/api/textos/documentos/${id}/conteudo.docx/${version}?` +
                       `${this.accessTokenParam()}`;
            });
        }

        public recuperarUrlArquivoApi(): string {
            return `${this.properties.onlyofficeUrl}/web-apps/apps/api/documents/api.js`;
        }
        
        public recuperarUrlPlugins(context: string): IPromise<string> {
            return this.$q.when(`${this.properties.onlyofficePluginsUrl}`);
        }
        
        public recuperarInfo(numeroEdicao: string, context: string): IPromise<any> {
            return this.$http.get(`${this.properties.apiUrl}/${context}/api/textos/command-service/${numeroEdicao}/info`)
                .then((response) => {
                return response.data;
            });
        }
        
        public removerUsuariosEdicao(edicao: Edicao, context: string): IPromise<{}> {
            if (!edicao.usuarios || edicao.usuarios.length === 0) {
                return this.$q.when({});
            }
            let cmd = {
                key: edicao.numero,
                users: edicao.usuarios
            }
            return this.$http({
                method: "POST",
                url: `${this.properties.apiUrl}/${context}/api/textos/command-service/drop`,
                data: cmd,
                transformResponse: (data) => {
                    return { key: data };
                }
            }).then((response: IHttpResponse<{}>) => {
                return response.data;
            });
        }

        private getBaseUrl(context: string): IPromise<string> {
            return this.$http({
                method: "GET",
                cache: true,
                url: `${this.properties.apiUrl}/${context}/api/textos/url`,
                transformResponse: (data) => {
                    return { url: data };
                }
            }).then((response: any) => {
                return response.data.url;
            });
        }

        private tempUrl(temporario: boolean): string {
            return temporario ? "temporarios/" : "";
        }

        private accessTokenParam(): string {
            return `access_token=${this.$cookies.get("access_token")}`;
        }

        private saveParam(save: boolean): string {
            return `&save=${save ? "true" : "false"}`;
        }
        
        public generateJwtToken(config: DocsAPI.OnlyofficeConfig, context: string): ng.IPromise<JwtToken> {
            return this.$http.post(`${this.properties.apiUrl}/${context}/api/onlyoffice/jwt`, config)
                .then((response: ng.IHttpPromiseCallbackArg<JwtToken>) => {
                return response.data;
            });
        }
        
        public generateJwtTokenHistoryData(historyData, context: string): ng.IPromise<JwtToken> {
            return this.$http.post(`${this.properties.apiUrl}/${context}/api/onlyoffice/jwt/history-data`, historyData)
                .then((response: ng.IHttpPromiseCallbackArg<JwtToken>) => {
                return response.data;
            });
        }
        
        public recuperarHistorico(id: number | string, context: string): IPromise<History[]> {
            return this.$http.get(`${this.properties.apiUrl}/${context}/api/textos/documentos/${id}/historico`)
                .then((response: ng.IHttpPromiseCallbackArg<History[]>) => {
                return response.data;
            });
        }

        public isSecure(): boolean {
            return this.properties.onlyofficeSecure;
        }

        public isHistoryEnabled(context: string): ng.IPromise<boolean> {
            return this.$http.get(`${this.properties.apiUrl}/${context}/api/textos/history/enabled`)
                .then((response: ng.IHttpPromiseCallbackArg<boolean>) => {
                return response.data;
            });
        }

        public restaurarVersao(id: number | string, version: number, context: string): ng.IPromise<{}> {
            return this.$http.post(
               `${this.properties.apiUrl}/${context}/api/textos/documentos/${id}/restore/${version}`,{});
        }

    }

    angular.module("app.documentos").service("app.documentos.OnlyofficeService", OnlyofficeService);
}